import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  IconButton,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridRowSelectionModel,
} from '@mui/x-data-grid';
import { Machine } from '../../types/machine';
import { SparePart, CreateSparePartRequest, UpdateSparePartRequest } from '../../types/inventory';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  fetchSpareParts,
  createSparePart,
  updateSparePart,
  deleteSparePart,
} from '../../store/slices/inventorySlice';
import { StatusBadge } from '../inventory/StatusBadge';
import { LowStockIndicator, filterLowStockItems } from '../inventory/LowStockIndicator';
import { BulkActionsToolbar } from '../inventory/BulkActionsToolbar';

interface MachineSparePartsTabProps {
  machine: Machine;
}

export const MachineSparePartsTab: React.FC<MachineSparePartsTabProps> = ({ machine }) => {
  const dispatch = useAppDispatch();
  const { items: spareParts, isLoading, isCreating } = useAppSelector((state) => state.inventory.spareParts);
  const { user } = useAppSelector((state) => state.auth);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<SparePart | null>(null);
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);

  // Form state
  const [formData, setFormData] = useState<CreateSparePartRequest>({
    part_number: '',
    name: '',
    description: '',
    quantity: 0,
    min_quantity: 0,
    unit: 'Stk.',
    supplier: '',
    price: undefined,
  });

  const canEdit = user?.role === 'admin';
  const machineSpareParts = spareParts.filter(sp => sp.machine_id === machine.id);
  const displayedParts = showLowStockOnly
    ? filterLowStockItems(machineSpareParts)
    : machineSpareParts;

  useEffect(() => {
    dispatch(fetchSpareParts({ machineId: machine.id }));
  }, [dispatch, machine.id]);

  const handleOpenDialog = (item?: SparePart) => {
    if (item) {
      setSelectedItem(item);
      setFormData({
        part_number: item.part_number,
        name: item.name,
        description: item.description || '',
        quantity: item.quantity,
        min_quantity: item.min_quantity,
        unit: item.unit,
        supplier: item.supplier || '',
        price: item.price,
      });
    } else {
      setSelectedItem(null);
      setFormData({
        part_number: '',
        name: '',
        description: '',
        quantity: 0,
        min_quantity: 0,
        unit: 'Stk.',
        supplier: '',
        price: undefined,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedItem(null);
  };

  const handleSave = async () => {
    if (selectedItem) {
      await dispatch(updateSparePart({
        id: selectedItem.id,
        data: formData as UpdateSparePartRequest,
      }));
    } else {
      await dispatch(createSparePart({
        machineId: machine.id,
        data: formData,
      }));
    }
    handleCloseDialog();
    dispatch(fetchSpareParts({ machineId: machine.id }));
  };

  const handleDelete = async (id: string) => {
    await dispatch(deleteSparePart(id));
    setDeleteDialogOpen(false);
    setSelectedItem(null);
    dispatch(fetchSpareParts({ machineId: machine.id }));
  };

  const columns: GridColDef[] = [
    {
      field: 'part_number',
      headerName: 'Teilenummer',
      width: 130,
      editable: false,
    },
    {
      field: 'name',
      headerName: 'Name',
      width: 200,
      editable: false,
    },
    {
      field: 'quantity',
      headerName: 'Bestand',
      width: 150,
      renderCell: (params) => (
        <LowStockIndicator
          quantity={params.row.quantity}
          minQuantity={params.row.min_quantity}
          unit={params.row.unit}
          inline
        />
      ),
    },
    {
      field: 'min_quantity',
      headerName: 'Mindestbestand',
      width: 130,
      valueFormatter: (value: any, row: any) => `${value} ${row.unit || ''}`,
    },
    {
      field: 'unit',
      headerName: 'Einheit',
      width: 90,
    },
    {
      field: 'supplier',
      headerName: 'Lieferant',
      width: 150,
    },
    {
      field: 'price',
      headerName: 'Preis',
      width: 100,
      valueFormatter: (value: any) => value ? `€${value.toFixed(2)}` : '-',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => (
        <StatusBadge type="spare-part" value={params.value} />
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Aktionen',
      width: 100,
      getActions: (params) => {
        const actions = [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Bearbeiten"
            onClick={() => handleOpenDialog(params.row as SparePart)}
            disabled={!canEdit}
          />,
        ];
        if (canEdit) {
          actions.push(
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Löschen"
              onClick={() => {
                setSelectedItem(params.row as SparePart);
                setDeleteDialogOpen(true);
              }}
            />
          );
        }
        return actions;
      },
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Ersatzteile ({displayedParts.length})
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant={showLowStockOnly ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setShowLowStockOnly(!showLowStockOnly)}
          >
            {showLowStockOnly ? 'Alle anzeigen' : 'Nur niedriger Bestand'}
          </Button>
          <IconButton
            onClick={() => dispatch(fetchSpareParts({ machineId: machine.id }))}
            size="small"
          >
            <RefreshIcon />
          </IconButton>
          {canEdit && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Hinzufügen
            </Button>
          )}
        </Box>
      </Box>

      {/* Low Stock Alert */}
      {filterLowStockItems(machineSpareParts).length > 0 && !showLowStockOnly && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {filterLowStockItems(machineSpareParts).length} Ersatzteil(e) mit niedrigem Bestand
        </Alert>
      )}

      {/* Bulk Actions Toolbar */}
      {selectedRows.length > 0 && canEdit && (
        <BulkActionsToolbar
          selectedCount={selectedRows.length}
          onClear={() => setSelectedRows([])}
          inventoryType="spare-part"
        />
      )}

      {/* Data Grid */}
      <Box sx={{ height: 500, width: '100%' }}>
        <DataGrid
          rows={displayedParts}
          columns={columns}
          loading={isLoading}
          checkboxSelection={canEdit}
          disableRowSelectionOnClick
          rowSelectionModel={selectedRows}
          onRowSelectionModelChange={(newSelection) => setSelectedRows(newSelection)}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          sx={{
            '& .MuiDataGrid-row:hover': {
              cursor: 'pointer',
            },
          }}
        />
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedItem ? 'Ersatzteil bearbeiten' : 'Ersatzteil hinzufügen'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Teilenummer"
                value={formData.part_number}
                onChange={(e) => setFormData({ ...formData, part_number: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Beschreibung"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Bestand"
                type="number"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: Number(e.target.value) })}
                InputProps={{ inputProps: { min: 0 } }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Mindestbestand"
                type="number"
                value={formData.min_quantity}
                onChange={(e) => setFormData({ ...formData, min_quantity: Number(e.target.value) })}
                InputProps={{ inputProps: { min: 0 } }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Einheit"
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Lieferant"
                value={formData.supplier}
                onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Preis (€)"
                type="number"
                value={formData.price || ''}
                onChange={(e) => setFormData({ ...formData, price: e.target.value ? Number(e.target.value) : undefined })}
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Abbrechen</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.part_number || !formData.name || isCreating}
          >
            {selectedItem ? 'Speichern' : 'Hinzufügen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Ersatzteil löschen</DialogTitle>
        <DialogContent>
          <Typography>
            Möchten Sie das Ersatzteil "{selectedItem?.name}" wirklich löschen?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Abbrechen</Button>
          <Button
            onClick={() => selectedItem && handleDelete(selectedItem.id)}
            variant="contained"
            color="error"
          >
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};